import { ElementBuilder } from "@universityofmaryland/web-builder-library";
import * as token from "@universityofmaryland/web-token-library";
import { card } from "@universityofmaryland/web-elements-library/composite";
import { gridOffset, gridGap } from "@universityofmaryland/web-elements-library/layout";
import { LoadingState } from "../../states/loading.js";
import { EmptyState } from "../../states/empty.js";
import { PaginationState } from "../../states/pagination.js";
import { Announcer } from "../../states/announcer.js";
import { newsFetchStrategy } from "../../strategies/fetch/news.js";
import { newsDisplayStrategy } from "../../strategies/display/news.js";
import { dispatch, eventNames } from "../../helpers/events/index.js";
import { setShadowStyles } from "../../helpers/styles/shadow.js";
const INITIAL_ITEMS = 3;
const LOAD_MORE_ITEMS = 2;
const createFetchProps = (props, offset) => ({
  token: props.token,
  categories: props.categories,
  isUnion: props.isUnion,
  numberOfRowsToStart: offset === 0 ? INITIAL_ITEMS : LOAD_MORE_ITEMS,
  numberOfColumnsToShow: 1,
  getOffset: () => offset
});
const createImageConfig = (entry) => {
  const imageUrl = entry.image?.[0]?.url;
  const altText = entry.image?.[0]?.altText;
  if (!imageUrl || !altText) return null;
  return {
    imageUrl,
    altText,
    linkUrl: entry.url,
    linkLabel: "Maryland Today Article with image"
  };
};
const createAnnouncerMessage = (offset, total, isLazyLoad) => {
  return isLazyLoad ? `Showing ${offset} of ${total} articles` : `Showing ${offset} articles`;
};
class FeaturedFeedState {
  /**
   * Initialize state with initial styles
   *
   * @param initialStyles - Initial CSS styles
   */
  constructor(initialStyles) {
    this.stylesArray = [];
    this.shadowRoot = null;
    this.totalEntries = 0;
    this.offset = 0;
    this.hasRenderedOffset = false;
    this.pagination = null;
    this.stylesArray.push(initialStyles);
  }
  /**
   * Add styles to the accumulated styles
   *
   * @param styles - CSS styles to add
   */
  addStyles(styles) {
    this.stylesArray.push(styles);
  }
  /**
   * Set shadow root reference for style updates
   *
   * @param shadow - Shadow root element
   */
  setShadowRoot(shadow) {
    this.shadowRoot = shadow;
  }
  /**
   * Update shadow DOM styles
   *
   * @returns Promise that resolves when styles are updated
   */
  async updateShadowStyles() {
    if (!this.shadowRoot) return;
    await setShadowStyles({
      shadowRoot: this.shadowRoot,
      styles: this.getStyles()
    });
  }
  /**
   * Get accumulated styles as single string
   *
   * @returns Combined CSS styles
   */
  getStyles() {
    return this.stylesArray.join("\n");
  }
  /**
   * Get shadow root callback for events
   *
   * @returns Callback function for shadow root
   */
  getShadowCallback() {
    return (shadow) => this.setShadowRoot(shadow);
  }
  /**
   * Get current offset
   *
   * @returns Current offset
   */
  getOffset() {
    return this.offset;
  }
  /**
   * Set offset to specific value
   *
   * @param value - New offset value
   */
  setOffset(value) {
    this.offset = value;
  }
  /**
   * Increment offset by count
   *
   * @param count - Number to increment by
   */
  incrementOffset(count) {
    this.offset += count;
  }
  /**
   * Get total entries
   *
   * @returns Total entries
   */
  getTotalEntries() {
    return this.totalEntries;
  }
  /**
   * Set total entries
   *
   * @param total - Total entries
   */
  setTotalEntries(total) {
    this.totalEntries = total;
  }
  /**
   * Check if offset layout has been rendered
   *
   * @returns True if offset layout rendered
   */
  hasOffset() {
    return this.hasRenderedOffset;
  }
  /**
   * Mark offset layout as rendered
   */
  markOffsetRendered() {
    this.hasRenderedOffset = true;
  }
  /**
   * Get pagination state
   *
   * @returns Pagination state or null
   */
  getPagination() {
    return this.pagination;
  }
  /**
   * Set pagination state
   *
   * @param pagination - Pagination state
   */
  setPagination(pagination) {
    this.pagination = pagination;
  }
}
const createOverlayCard = (entry, state, isThemeDark) => {
  const overlayCard = newsDisplayStrategy.mapEntryToCard(entry, {
    isOverlay: true,
    isThemeDark,
    imageConfig: () => createImageConfig(entry)
  });
  state.addStyles(`
    ${overlayCard.styles}

    .${card.overlay.imageClassRef} {
      height: inherit;
    }

    @media (${token.media.queries.tablet.min}) {
      .${card.overlay.imageClassRef} .card-overlay-image-container {
        min-height: 560px;
      }
    }

    .${card.overlay.imageClassRef} .umd-asset-image-wrapper-scaled {
      position: absolute;
    }
  `);
  return overlayCard;
};
const createBlockCards = (entries, state, options) => {
  return entries.map((entry) => {
    const blockCard = newsDisplayStrategy.mapEntryToCard(entry, {
      isThemeDark: options.isThemeDark,
      isTransparent: options.isTransparent,
      isAligned: true,
      imageConfig: () => createImageConfig(entry)
    });
    state.addStyles(blockCard.styles);
    return blockCard;
  });
};
const renderFeaturedLayout = async (container, entries, state, props, loadMore) => {
  const {
    isThemeDark = false,
    isTransparent = false,
    isLayoutReversed = false,
    overwriteStickyPosition,
    isLazyLoad = false
  } = props;
  if (entries.length < 2 || state.hasOffset()) {
    return renderStandardGrid(container, entries, state, {
      isThemeDark,
      isTransparent
    });
  }
  state.markOffsetRendered();
  const offsetLayout = gridOffset({
    columns: 2,
    isLayoutReversed,
    stickyTopPosition: overwriteStickyPosition
  });
  const gridLayout = gridGap({ columns: 2 });
  gridLayout.element.setAttribute("id", "umd-featured-news-grid-container");
  const overlayCard = createOverlayCard(entries[0], state, isThemeDark);
  const blockCards = createBlockCards(entries.slice(1, 3), state, {
    isThemeDark,
    isTransparent
  });
  blockCards.forEach((card2) => {
    gridLayout.element.appendChild(card2.element);
  });
  offsetLayout.element.appendChild(overlayCard.element);
  offsetLayout.element.appendChild(gridLayout.element);
  container.appendChild(offsetLayout.element);
  state.addStyles(offsetLayout.styles);
  state.addStyles(gridLayout.styles);
  state.setOffset(3);
  if (isLazyLoad && state.getTotalEntries() > state.getOffset()) {
    const pagination = new PaginationState({
      totalEntries: state.getTotalEntries(),
      offset: state.getOffset(),
      isLazyLoad: true,
      callback: loadMore
    });
    const paginationElement = pagination.render(container);
    if (paginationElement) state.addStyles(paginationElement.styles);
    state.setPagination(pagination);
  }
  const message = createAnnouncerMessage(
    INITIAL_ITEMS,
    state.getTotalEntries(),
    isLazyLoad
  );
  const announcer = new Announcer({ message });
  container.appendChild(announcer.getElement());
  await state.updateShadowStyles();
};
const renderStandardGrid = async (container, entries, state, options) => {
  let gridContainer = container.querySelector(
    "#umd-featured-news-grid-container"
  );
  if (!gridContainer) {
    const gridLayout = gridGap({ columns: 2 });
    gridLayout.element.setAttribute("id", "umd-featured-news-grid-container");
    container.appendChild(gridLayout.element);
    state.addStyles(gridLayout.styles);
    gridContainer = gridLayout.element;
  }
  const blockCards = createBlockCards(entries, state, options);
  blockCards.forEach((card2) => {
    gridContainer.appendChild(card2.element);
  });
  state.incrementOffset(entries.length);
  await state.updateShadowStyles();
};
const renderError = async (container, message, state, isThemeDark) => {
  const emptyState = new EmptyState({ message, isThemeDark });
  emptyState.render(container);
  state.addStyles(emptyState.styles);
  await state.updateShadowStyles();
};
const newsFeatured = (props) => {
  const {
    token: token2,
    categories,
    isUnion,
    isThemeDark = false,
    isLazyLoad = false,
    isTransparent = false
  } = props;
  const containerBuilder = new ElementBuilder("div").withClassName(
    "featured-news-feed"
  );
  const container = containerBuilder.getElement();
  const loading = new LoadingState({ isThemeDark });
  const state = new FeaturedFeedState(loading.styles);
  const loadMore = async () => {
    const pagination = state.getPagination();
    if (pagination) {
      pagination.remove();
    }
    loading.show(container);
    const fetchProps = createFetchProps(
      { token: token2, categories, isUnion },
      state.getOffset()
    );
    const variables = newsFetchStrategy.composeApiVariables(fetchProps);
    const entries = await newsFetchStrategy.fetchEntries(variables);
    loading.hide();
    if (!entries || entries.length === 0) return;
    await renderStandardGrid(container, entries, state, {
      isThemeDark,
      isTransparent
    });
    if (pagination) {
      pagination.updateState(state.getOffset(), state.getTotalEntries());
      if (pagination.styles) state.addStyles(pagination.styles);
      await state.updateShadowStyles();
    }
    const existingAnnouncer = container.querySelector(
      '[role="status"]'
    );
    if (existingAnnouncer) {
      existingAnnouncer.textContent = createAnnouncerMessage(
        state.getOffset(),
        state.getTotalEntries(),
        isLazyLoad
      );
    }
    dispatch(
      container,
      eventNames.FEED_LOADED_MORE,
      {
        items: entries,
        count: entries.length,
        total: state.getTotalEntries()
      }
    );
  };
  const initialize = async () => {
    loading.show(container);
    const fetchProps = createFetchProps({ token: token2, categories, isUnion }, 0);
    const variables = newsFetchStrategy.composeApiVariables(fetchProps);
    const [count, entries] = await Promise.all([
      newsFetchStrategy.fetchCount(variables),
      newsFetchStrategy.fetchEntries(variables)
    ]);
    loading.hide();
    if (!entries || entries.length === 0) {
      await renderError(
        container,
        "No news articles found",
        state,
        isThemeDark
      );
      return;
    }
    state.setTotalEntries(count || entries.length);
    dispatch(container, eventNames.FEED_LOADED, {
      items: entries,
      count: entries.length,
      total: state.getTotalEntries()
    });
    await renderFeaturedLayout(container, entries, state, props, loadMore);
  };
  initialize();
  const model = containerBuilder.build();
  const setPosition = (position) => {
    const overlayElement = container.querySelector(
      `.${card.overlay.imageClassRef}`
    );
    if (overlayElement) overlayElement.style.top = `${position}px`;
  };
  return {
    element: model.element,
    get styles() {
      return state.getStyles();
    },
    events: {
      callback: state.getShadowCallback(),
      setPosition
      // Custom event for sticky position control
    }
  };
};
export {
  newsFeatured
};
//# sourceMappingURL=featured.js.map
